import * as React from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Localizer } from "@/main/react/Types";
import { navigateToUrl } from "@/main/common/BrowserNavigation";
import { DeletedContentKind } from "@/main/react/page/profile-page/DeletedContentRestoreRow";
import { isPageInterceptableClick } from "@/main/navigation/NavigationPolicy";

/**
 * The visible door to the author's own recovery list.
 *
 * <p>Added 2026-08-14, after the member this feature was built for deleted a poem by mistake, was
 * pointed at the recovery list, and could not find it. Nothing was broken: the row was intact, the
 * restore verb accepted it, and the deleted-tab query returned it FIRST. The prod ACCESS log showed he
 * loaded `/my_dashboard/discussions/authored` four times and `/my_dashboard/discussions/saved` once,
 * never `/my_dashboard/discussions/deleted`, with zero errors in the window. **The sub-tab select was
 * the only door and it does not look like one** (`DASHBOARD-6`).
 *
 * <p>Two properties are load-bearing and should survive any restyle:
 * <ul>
 *   <li><b>It is visible, not just named.</b> Giving the sub-tab select an `aria-label` landed in the
 *       same task and does not replace this: the member who cannot find the list is not usually the one
 *       using a screen reader, he is the one who cannot tell a pill is a control.</li>
 *   <li><b>It renders on the PROFILE too, owner-only.</b> "Mi perfil" is where a member goes looking for
 *       their own posts, and the recovery list lives on a different page entirely. The profile shows the
 *       door; the dashboard owns the list.</li>
 * </ul>
 *
 * <p>Reuses `dashboard.subtab.deleted`, already translated in all 25 catalogs, so this mints no copy.
 *
 * <p>Design: `designs/01-now/2026-08-12-author-recoverable-deleted-content-design.md`, 2026-08-14
 * addendum.
 */
type Props = {
    kind: DeletedContentKind;
    localizer: Localizer;
    source: string;
};

export function deletedContentHref(kind: DeletedContentKind): string {
    return kind === "discussion"
        ? "/my_dashboard/discussions/deleted"
        : "/my_dashboard/arguments/deleted";
}

export function DeletedContentEntryPointButton({ kind, localizer, source }: Props) {
    const href = deletedContentHref(kind);

    return (
        // `EMPTY-STATES-82` — this control computed a real address and threw it away.
        //
        // `asChild` renders the pill AS the anchor, so every class, the icon and the two hooks are
        // byte-identical to the button it replaces: this is the same control, and a second visual
        // recipe for it is how two families drift apart. What changes is that the destination is now
        // IN the element, so a screen reader announces a link rather than "Deleted, button", and
        // middle-click, Cmd/Ctrl-click, hover preview and "copy link address" all work — on a pill
        // whose entire purpose is getting a member to a list they could not find.
        //
        // The handler still runs on exactly the clicks it always did. `preventDefault()` is called
        // only for an unmodified primary click; a middle-click never reaches a React `onClick` at all,
        // and a modified click falls through to the browser with the real address. So the soft
        // navigation below is unchanged, `navigateToUrl` still owns it, and rule 33 still has nothing
        // to bind: this is a real navigation to a different page, not an in-page history push.
        <Button
            asChild
            variant="outline"
            size="sm"
            className="rounded-full"
        >
            <a
                href={href}
                data-playwright-id="deleted-content-entry-point"
                data-deleted-content-kind={kind}
                onClick={(event) => {
                    if (!isPageInterceptableClick(event)) {
                        return;
                    }
                    event.preventDefault();
                    event.stopPropagation();
                    navigateToUrl(href, { source });
                }}
            >
                <Trash2 className="size-4" aria-hidden="true" />
                {localizer("dashboard.subtab.deleted")}
            </a>
        </Button>
    );
}
